import React, { useState, useRef, useEffect } from 'react';
import { normalizeFeatureValue } from '../../utils/featureNormalization';
import { sortFeatureValues } from '../../utils/sortingUtils';

/**
 * DropdownInput - Standard dropdown select input
 * 
 * Supports:
 * - Single select (default)
 * - Multi-select (when isMultiSelect is true)
 * - Searchable (when allowSearch is true)
 * - Image display (when showImages is true)
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {*} props.value - Current selected value(s)
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.allowSearch - Enable search/filter
 * @param {boolean} props.showImages - Show images for values
 * @param {string} props.imageSize - Image size: 'thumbnail', 'small', 'medium', 'large'
 * @param {boolean} props.isMultiSelect - Allow multiple selections
 * @param {number} props.multiSelectMax - Maximum selections for multi-select
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const DropdownInput = ({
    feature,
    value,
    onChange,
    allowSearch = false,
    showImages = false,
    imageSize = 'thumbnail',
    isMultiSelect = false,
    multiSelectMax = 5,
    placeholder = 'Select an option...',
    disabled = false,
    isOptionDisabled,
    className = ''
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    
    const values = sortFeatureValues((feature?.values || []).map(normalizeFeatureValue));
    const hasImages = showImages && values.some(v => v.imageUrl);
    
    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    // Handle keyboard navigation
    useEffect(() => {
        if (!isOpen) return;
        
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);
    
    const filteredValues = allowSearch && searchTerm
        ? values.filter(v => 
            v.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.displayText?.toLowerCase().includes(searchTerm.toLowerCase()))
        : values;
    
    const handleSelect = (selectedValue) => {
        if (isMultiSelect) {
            // Multi-select logic
            const currentValues = Array.isArray(value) ? value : [];
            
            if (currentValues.includes(selectedValue)) {
                // Remove from selection
                const newValues = currentValues.filter(v => v !== selectedValue);
                onChange({ target: { value: newValues } });
            } else {
                // Add to selection (check max)
                if (currentValues.length < multiSelectMax) {
                    const newValues = [...currentValues, selectedValue];
                    onChange({ target: { value: newValues } });
                }
            }
        } else {
            // Single select
            setIsOpen(false);
            setSearchTerm('');
            onChange({ target: { value: selectedValue } });
        }
    };
    
    const getImageSizeStyle = () => {
        switch (imageSize) {
            case 'thumbnail': return { width: '24px', height: '24px' };
            case 'small': return { width: '32px', height: '32px' };
            case 'medium': return { width: '40px', height: '40px' };
            case 'large': return { width: '48px', height: '48px' };
            default: return { width: '32px', height: '32px' };
        }
    };
    
    const renderValueDisplay = () => {
        if (isMultiSelect) {
            const selectedValues = Array.isArray(value) ? value : [];
            if (selectedValues.length === 0) {
                return <span style={{ color: 'var(--text-secondary)' }}>{placeholder}</span>;
            }
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {selectedValues.map(v => {
                        const matchedValue = values.find(x => x.value === String(v));
                        return (
                            <span 
                                key={v}
                                style={{
                                    background: 'var(--accent)',
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                {hasImages && matchedValue?.imageUrl && (
                                    <img 
                                        src={matchedValue.imageUrl} 
                                        alt="" 
                                        style={{ ...getImageSizeStyle(), objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                )}
                                {matchedValue?.displayText || matchedValue?.value || v}
                            </span>
                        );
                    })}
                </div>
            );
        } else {
            const matchedValue = values.find(v => v.value === String(value));
            if (!value) {
                return <span style={{ color: 'var(--text-secondary)' }}>{placeholder}</span>;
            }
            return (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {hasImages && matchedValue?.imageUrl && (
                        <img 
                            src={matchedValue.imageUrl} 
                            alt="" 
                            style={{ ...getImageSizeStyle(), objectFit: 'cover', borderRadius: '4px' }}
                        />
                    )}
                    {matchedValue?.displayText || matchedValue?.value || value}
                </span>
            );
        }
    };
    
    const isSelected = (val) => {
        if (isMultiSelect) {
            const currentValues = Array.isArray(value) ? value : [];
            return currentValues.map(v => String(v)).includes(val.value);
        }
        return val.value === String(value);
    };
    
    const isAtMaxSelection = () => {
        if (!isMultiSelect) return false;
        const currentValues = Array.isArray(value) ? value : [];
        return currentValues.length >= multiSelectMax;
    };
    
    return (
        <div 
            ref={dropdownRef}
            className="dropdown-input-wrapper"
            style={{ position: 'relative' }}
        >
            <button
                type="button"
                className={className}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                style={{
                    width: '100%',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                    opacity: disabled ? 0.6 : 1
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                    {renderValueDisplay()}
                </div>
                <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: '0.8rem' }} />
            </button>
            
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    {allowSearch && (
                        <div style={{ padding: '8px', borderBottom: '1px solid var(--border)' }}>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search..."
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid var(--border)',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem'
                                }}
                                autoFocus
                            />
                        </div>
                    )}
                    
                    <div style={{ padding: '4px' }}>
                        {filteredValues.length === 0 ? (
                            <div style={{ 
                                padding: '12px', 
                                textAlign: 'center',
                                color: 'var(--text-secondary)',
                                fontSize: '0.85rem'
                            }}>
                                No options found
                            </div>
                        ) : (
                            filteredValues.map((val) => {
                                const valKey = val.value;
                                const display = val.displayText || val.value;
                                const isSel = isSelected(val);
                                const isDisabledOption = (isAtMaxSelection() && !isSel && isMultiSelect)
                                    || Boolean(isOptionDisabled?.(valKey));
                                
                                return (
                                    <button
                                        key={val.id || valKey}
                                        type="button"
                                        onClick={() => !isDisabledOption && handleSelect(valKey)}
                                        disabled={isDisabledOption}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: isDisabledOption ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            textAlign: 'left',
                                            opacity: isDisabledOption ? 0.5 : 1,
                                            transition: 'background-color 0.15s',
                                            ...(isSel ? { 
                                                backgroundColor: 'rgba(0, 30, 98, 0.1)',
                                                color: 'var(--text-primary)'
                                            } : { 
                                                color: 'var(--text-primary)',
                                                '&:hover': { backgroundColor: 'var(--bg-secondary)' }
                                            })
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isDisabledOption) {
                                                e.target.style.backgroundColor = 'var(--bg-secondary)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSel && !isDisabledOption) {
                                                e.target.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        {hasImages && val.imageUrl && (
                                            <img 
                                                src={val.imageUrl} 
                                                alt="" 
                                                style={{ 
                                                    ...getImageSizeStyle(), 
                                                    objectFit: 'cover', 
                                                    borderRadius: '4px',
                                                    flexShrink: 0
                                                }}
                                            />
                                        )}
                                        <span style={{ flex: 1 }}>{display}</span>
                                        {isSel && (
                                            <i className="fa-solid fa-check" style={{ 
                                                color: 'var(--accent)', 
                                                fontSize: '0.8rem' 
                                            }} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                    
                    {isMultiSelect && Array.isArray(value) && value.length > 0 && (
                        <div style={{
                            padding: '8px',
                            borderTop: '1px solid var(--border)',
                            background: 'var(--bg-secondary)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                {value.length} of {multiSelectMax} selected
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    onChange({ target: { value: [] } });
                                    setIsOpen(false);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--error)',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                }}
                            >
                                Clear All
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DropdownInput;
